import { SplitContextProvider, createSplitContext, useSplitContext } from "@styleguide";
import type { Dispatch, ReactNode } from "react";
import type { FieldValues } from "react-hook-form";
import { type IUseUnsavedChangesGuardFormOptions, useUnsavedChangesGuardForm } from "./form";
import { useUnsavedChangesGuardLogic } from "./logics";

export interface IUnsavedChangesGuardContext {
  isDirty: boolean;
  setIsDirty: Dispatch<boolean>;
  openConfirmationModal: boolean;
  setOpenConfirmationModal: Dispatch<boolean>;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  saveLoading: boolean;
  handleSaveAndLeave: () => Promise<void>;
  setValidateBeforeLeave: (fn: (() => boolean | Promise<boolean>) | null) => void;
  setOnReset: (fn: (() => void) | null) => void;
  setOnSubmit: (fn: (() => Promise<void>) | null) => void;
}

const splitContext = createSplitContext<IUnsavedChangesGuardContext>({
  isDirty: false,
  setIsDirty: () => undefined,
  openConfirmationModal: false,
  setOpenConfirmationModal: () => undefined,
  onConfirm: () => undefined,
  onCancel: () => undefined,
  onClose: () => undefined,
  saveLoading: false,
  handleSaveAndLeave: async () => undefined,
  setValidateBeforeLeave: () => undefined,
  setOnReset: () => undefined,
  setOnSubmit: () => undefined,
});

export const useUnsavedChangesGuardSplitContext = <K extends keyof IUnsavedChangesGuardContext>(key: K) => {
  return useSplitContext(splitContext, key);
};

interface IUnsavedChangesGuardContextProviderProps<T extends FieldValues = FieldValues> {
  children: ReactNode;
  form?: IUseUnsavedChangesGuardFormOptions<T>;
}

export const UnsavedChangesGuardContextProvider = <T extends FieldValues = FieldValues>({
  children,
  form,
}: IUnsavedChangesGuardContextProviderProps<T>) => {
  const logicValues = useUnsavedChangesGuardLogic();

  return (
    <SplitContextProvider contextsMap={splitContext} values={logicValues}>
      {form && <UnsavedChangesGuardFormRegistration form={form} />}
      {children}
    </SplitContextProvider>
  );
};

const UnsavedChangesGuardFormRegistration = <T extends FieldValues>({
  form,
}: {
  form: IUseUnsavedChangesGuardFormOptions<T>;
}) => {
  useUnsavedChangesGuardForm(form);
  return null;
};
